import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing } from 'lucide-react';
import { EMPTY_STATES } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Button, Card, EmptyState, ErrorNote, Spinner } from '@/components/ui';
import { api, apiData, type ApiResult } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import { deepLinkFor, enableWebPush, webPushSupported } from '@/lib/push';
import { useAuth } from '@/store/auth';
import type { NotificationItem } from '@/types';

/** 인앱 알림센터 — 명세서 §5.17 / §8 */
export function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const membership = useAuth((s) => s.membership);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<NotificationItem[]>('/notifications?limit=50'),
  });

  const markRead = useMutation({
    mutationFn: (id: string) => apiData(`/notifications/${id}/read`, { method: 'PATCH' }),
    onSuccess: (_result, id) => {
      // 목록 전체 재조회 대신 해당 알림만 로컬에서 읽음 처리한다
      queryClient.setQueryData<ApiResult<NotificationItem[]>>(['notifications'], (prev) =>
        prev
          ? {
              ...prev,
              data: prev.data.map((n) =>
                n.id === id ? { ...n, readAt: n.readAt ?? new Date().toISOString() } : n,
              ),
            }
          : prev,
      );
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => apiData('/notifications/read-all', { method: 'PATCH' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const notifications = data?.data ?? [];
  const unreadCount = notifications.filter((n) => !n.readAt).length;

  function open(notification: NotificationItem) {
    if (!notification.readAt) markRead.mutate(notification.id);
    const path = deepLinkFor(notification.target ?? undefined, membership?.role);
    if (path) navigate(path);
  }

  return (
    <Screen>
      <AppBar
        title="알림"
        right={
          unreadCount > 0 ? (
            <Button size="sm" variant="ghost" onClick={() => markAllRead.mutate()}>
              모두 읽음
            </Button>
          ) : undefined
        }
      />

      <WebPushBanner />

      {isPending && <Spinner />}
      {isError && <ErrorNote message="알림을 불러오지 못했어요." onRetry={() => void refetch()} />}
      {!isPending && notifications.length === 0 && (
        <EmptyState title={EMPTY_STATES.notification.title} body={EMPTY_STATES.notification.body} />
      )}

      <ul className="flex flex-col gap-2 p-4">
        {notifications.map((notification) => (
          <Card
            key={notification.id}
            as="li"
            className={notification.readAt ? 'p-0 opacity-70' : 'p-0'}
          >
            <button
              type="button"
              onClick={() => open(notification)}
              className="flex w-full items-start gap-3 p-4 text-left"
            >
              <span
                className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                  notification.readAt ? 'bg-line-strong' : 'bg-primary'
                }`}
                aria-hidden
              />
              <span className="min-w-0 flex-1">
                <span className="block font-semibold">{notification.title}</span>
                <span className="block text-base text-ink-sub">{notification.body}</span>
                <span className="mt-1 block text-sm text-ink-sub">
                  {formatRelative(notification.createdAt)}
                </span>
              </span>
            </button>
          </Card>
        ))}
      </ul>
    </Screen>
  );
}

/**
 * 브라우저 알림 켜기 (§8) — 웹·PWA에서 푸시를 받으려면 사용자 동작으로
 * 권한을 받아야 한다. 이미 허용했거나 미지원이면 조용히 사라진다.
 * 아이폰은 사파리에서 "홈 화면에 추가"한 뒤에만 지원된다(iOS 16.4+).
 */
function WebPushBanner() {
  const [status, setStatus] = useState<'idle' | 'busy' | 'done' | 'denied'>(() =>
    webPushSupported() && Notification.permission === 'default' ? 'idle' : 'done',
  );

  const { data } = useQuery({
    queryKey: ['web-push-key'],
    queryFn: () => apiData<{ publicKey: string | null }>('/notifications/web-push-key'),
    staleTime: Infinity,
  });

  if (status === 'done' || !data?.publicKey) return null;

  return (
    <div className="px-4 pt-3">
      <Card className="flex items-center gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary-soft text-primary-strong">
          <BellRing size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">브라우저 알림 켜기</p>
          <p className="text-sm text-ink-sub">
            {status === 'denied'
              ? '알림이 거부되어 있어요. 브라우저 설정에서 허용해 주세요.'
              : '앱을 열지 않아도 새 소식을 바로 받아요.'}
          </p>
        </div>
        {status !== 'denied' && (
          <Button
            size="sm"
            loading={status === 'busy'}
            onClick={() => {
              setStatus('busy');
              void enableWebPush().then((ok) => setStatus(ok ? 'done' : 'denied'));
            }}
          >
            켜기
          </Button>
        )}
      </Card>
    </div>
  );
}
